import { useMemo, useState } from 'react';
import { AIRLINES, airlineBookingUrl, flightsFor } from '@shared/flights';
import type { FlightDirection } from '@shared/types';
import { formatLocalHm } from '@/lib/formatting';

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function FlightsPage() {
  const [direction, setDirection] = useState<FlightDirection>('outbound');
  const [airline, setAirline] = useState<string>('all');
  const [date, setDate] = useState(todayKey());
  const [picked, setPicked] = useState<string | null>(null);

  const flights = useMemo(
    () => flightsFor(direction, airline === 'all' ? undefined : airline),
    [direction, airline],
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Flights</h1>
        <p className="page-subtitle">Newark Liberty (EWR) ↔ Lisbon (LIS)</p>
      </div>

      <p className="muted" style={{ marginBottom: 12 }}>
        Curated EWR ↔ LIS — pick a direction, airline, and time. No live fares. FAO is not the default.
      </p>

      <div role="tablist" aria-label="Flight direction" className="direction-toggle">
        <button
          type="button"
          role="tab"
          aria-selected={direction === 'outbound'}
          className={`direction-toggle__btn ${direction === 'outbound' ? 'is-active' : ''}`}
          onClick={() => setDirection('outbound')}
        >
          EWR → LIS
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={direction === 'inbound'}
          className={`direction-toggle__btn ${direction === 'inbound' ? 'is-active' : ''}`}
          onClick={() => setDirection('inbound')}
        >
          LIS → EWR
        </button>
      </div>

      <div className="chip-row" role="group" aria-label="Airline">
        <button
          type="button"
          className={`interest-chip ${airline === 'all' ? 'interest-chip--on' : ''}`}
          aria-pressed={airline === 'all'}
          onClick={() => setAirline('all')}
        >
          All
        </button>
        {AIRLINES.map((a) => (
          <button
            key={a}
            type="button"
            className={`interest-chip ${airline === a ? 'interest-chip--on' : ''}`}
            aria-pressed={airline === a}
            onClick={() => setAirline(a)}
          >
            {a === 'TAP Air Portugal' ? 'TAP' : 'United'}
          </button>
        ))}
      </div>

      <label className="field">
        <span>Date</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>

      {flights.map((f) => (
        <article key={f.ident} className={`flight-card ${picked === f.ident ? 'is-picked' : ''}`}>
          <div className="flight-card__row">
            <strong>
              {f.ident} · {f.airline}
            </strong>
            <span>
              {f.origin} {formatLocalHm(f.departLocal)} → {f.destination} {formatLocalHm(f.arriveLocal)}
            </span>
          </div>
          <p className="muted">{f.duration}{f.notes ? ` · ${f.notes}` : ''}</p>
          <div className="action-row">
            <button type="button" className="action-btn" onClick={() => setPicked(f.ident)}>
              {picked === f.ident ? 'Selected' : 'Select this flight'}
            </button>
            <a className="action-btn" href={airlineBookingUrl(f, date)} target="_blank" rel="noreferrer">
              Open {f.airlineCode} booking
            </a>
          </div>
        </article>
      ))}

      {flights.length === 0 && <p className="empty-state">No stub flights for that filter.</p>}
    </div>
  );
}
